import { type ComponentPropsWithoutRef, type MouseEvent, type ReactNode } from 'react'

import { ButtonText } from '@/components/common/Button'
import { MenuTitle } from '@/components/common/MenuTitle'
import { Icon } from '@/components/icons/Icon'
import { type DistributiveOmit } from '@/defs/misc'
import { type SidebarType } from '@/defs/view'
import { useHeightAvailable } from '@/hooks/useHeightAvailable'
import { type MenuStatusHoveredType } from '@/store/view'
import { getClickawayIgnoreProps } from '@/utils/clickawayUtil'
import { cn } from '@/utils/commonUtil'

import styles from './SidebarRoot.module.scss'

type BaseProps = {
  children: ReactNode
  iconElement?: ReactNode
  onClickClose: () => void
  onClickLock: () => void
  position: 'left' | 'right'
  sidebar: Exclude<SidebarType, 'gallery'>
  status: MenuStatusHoveredType
  title: string
}

type Props = BaseProps & DistributiveOmit<ComponentPropsWithoutRef<'aside'>, keyof BaseProps>

export const SidebarRoot = ({
  children,
  className,
  iconElement,
  onClickClose: onClickCloseProp,
  onClickLock: onClickLockProp,
  position,
  sidebar,
  status,
  title,
  ...props
}: Props) => {
  const ref = useHeightAvailable()

  const onClickLock = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    onClickLockProp()
  }

  const onClickClose = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    onClickCloseProp()
  }

  return (
    <aside
      {...props}
      {...getClickawayIgnoreProps(['collapse-expanded'])}
      className={cn(styles.container, className, { position, sidebar, status, styles })}
      ref={ref}
    >
      <div className={styles.clip}>
        <div className={styles.outer}>
          <div className={styles.inner}>
            <div className={styles.content}>
              <MenuTitle icon={iconElement && <div className={styles.icon}>{iconElement}</div>}>
                <h2>{title}</h2>
                <ButtonText aria-label='Pin in place' colorScheme='light' onClick={onClickLock} size='lg'>
                  <span className={styles.button}>
                    <span className={styles.iconUnlocked}>
                      <Icon name='thumbtackSlash' />
                    </span>
                    <span className={styles.iconLocked}>
                      <Icon name='thumbtack' />
                    </span>
                  </span>
                </ButtonText>
                <ButtonText aria-label='Close' colorScheme='light' hover='warning' onClick={onClickClose} size='lg'>
                  <span className={styles.button}>
                    <Icon name='xMark' />
                  </span>
                </ButtonText>
              </MenuTitle>
              {children}
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
